import { createSelector } from '@reduxjs/toolkit';
import { selectJobSeeker, JobSeekerCommunityAdminRole } from 'features/job-seeker-auth';
import { OnBehalfOfOrganizationAdmin } from 'features/job-seeker-on-behalf-of-organization-admin/types';
import { selectJobSeekerOnBehalfOfOrganizationAdminId } from "features/job-seeker-on-behalf-of-organization-admin";
import { MemberRoles } from "features/types";
import { checkIsAtLeastOneCommunityFullAdmin, checkIsAtLeastOneCommunityViewOnlyAdmin, getAdminInCommunitiesSelectList, getProgramLeadInCommunitiesSelectList, getAdminRoleInCommunity } from 'utils/job-seeker-on-behalf-of-organization-helper';

export const selectJobSeekerRolesInCommunity = createSelector([selectJobSeeker], (jobSeeker) => {
  if (!jobSeeker) return [];
  const jobSeekerRoles: MemberRoles[] = [MemberRoles.Member];
  const leadProgramInCommunities = getProgramLeadInCommunitiesSelectList(jobSeeker);
  if (leadProgramInCommunities.length > 0) {
    jobSeekerRoles.push(MemberRoles.ProgramLead);
  }
  if (checkIsAtLeastOneCommunityFullAdmin(jobSeeker)) {
    jobSeekerRoles.push(MemberRoles.FullAdmin);
  }
  if (checkIsAtLeastOneCommunityViewOnlyAdmin(jobSeeker)) {
    jobSeekerRoles.push(MemberRoles.ViewOnlyAdmin);
  }
  return jobSeekerRoles;
});

export const selectAdminInCommunitiesSelectList = createSelector([selectJobSeeker], (jobSeeker): OnBehalfOfOrganizationAdmin[] => {
  if (!jobSeeker) return [];
  return getAdminInCommunitiesSelectList(jobSeeker);
});

export const selectgetProgramLeadInCommunitiesSelectList = createSelector([selectJobSeeker], (jobSeeker): OnBehalfOfOrganizationAdmin[] => {
  if (!jobSeeker) return [];
  return getProgramLeadInCommunitiesSelectList(jobSeeker);
});

export const selectIsAtLeastOneProgramLeadInCommunities = createSelector([selectgetProgramLeadInCommunitiesSelectList], (leadProgramInCommunities): boolean => {
  return leadProgramInCommunities.length > 0;
});

export const validateCommunityAdminContextRole = (allowedRoles: string[]) =>
  createSelector([selectJobSeekerOnBehalfOfOrganizationAdminId, selectAdminInCommunitiesSelectList], (onBehalfOfOrganizationAdminId, isAdminInCommunitiesSelectList): boolean => {
    if (!onBehalfOfOrganizationAdminId) return false;
    const adminRoleInCommunity = getAdminRoleInCommunity(onBehalfOfOrganizationAdminId, isAdminInCommunitiesSelectList);
    return adminRoleInCommunity ? allowedRoles.includes(adminRoleInCommunity) : false;
  });

export const validateCanAddEditPostByContext = (isNew: boolean, postedByAdminCommunityId: string | undefined) =>
  createSelector([selectJobSeekerOnBehalfOfOrganizationAdminId, selectAdminInCommunitiesSelectList], (onBehalfOfOrganizationAdminId, isAdminInCommunitiesSelectList): string => {
    if (!postedByAdminCommunityId && !onBehalfOfOrganizationAdminId) return '';
    let error = '';
    const adminRoleInCommunity = onBehalfOfOrganizationAdminId ? getAdminRoleInCommunity(onBehalfOfOrganizationAdminId, isAdminInCommunitiesSelectList) : undefined;
    if (postedByAdminCommunityId) {
      if (!onBehalfOfOrganizationAdminId || postedByAdminCommunityId !== onBehalfOfOrganizationAdminId) {
        error = adminRoleInCommunity && [JobSeekerCommunityAdminRole.FullAdmin].includes(adminRoleInCommunity)
          ? "To be authorized to edit this post, please select the corresponding community."
          : "You're not authorized to edit this post."
      } else {
        if (!adminRoleInCommunity || ![JobSeekerCommunityAdminRole.FullAdmin].includes(adminRoleInCommunity)) {
          error = "Your role is not sufficient to edit this post."
        }
      }
    } else if (!isNew && onBehalfOfOrganizationAdminId) {
      error = "you are not authorized to edit this post in admin view."

    } else if (onBehalfOfOrganizationAdminId && adminRoleInCommunity !== JobSeekerCommunityAdminRole.FullAdmin) {
      error = "Your role is not sufficient to post."
    }
    return error;
  });
